import { createSelector } from '@reduxjs/toolkit';
import { DataStatus, isFinished, isLoading } from '@/shared/lib';
import { createAppSlice } from '@/shared/model';
import * as datasourcesAPI from '../api/datasourcesAPI';
import {
  APP_STORE_CONNECT_REPORTING_INTEGRATION_ID,
  GOOGLE_PLAY_CONSOLE_REPORTING_INTEGRATION_ID,
} from './constants';
import type {
  AppStoreConnectReportingIntegration,
  GooglePlayConsoleReportingIntegration,
  Integration,
} from './types';

interface State {
  integrations: Integration[];
  status: DataStatus;
  requestedOrganizationId?: string;
}

const initialState: State = {
  integrations: [],
  status: DataStatus.idle,
  requestedOrganizationId: undefined,
};

export const datasourcesSlice = createAppSlice({
  name: 'datasources',
  initialState,
  reducers: (create) => ({
    setupIntegrations: create.asyncThunk<string, Integration[]>(
      () => {
        return datasourcesAPI.getIntegrations();
      },
      {
        pending: (state, action) => {
          state.status = DataStatus.loading;
          state.requestedOrganizationId = action.meta.arg;
        },
        fulfilled: (state, action) => {
          state.integrations = action.payload;
          state.status = DataStatus.finished;
        },
        rejected: (state) => {
          state.status = DataStatus.error;
          state.requestedOrganizationId = undefined;
        },
        options: {
          condition: (requestedOrganizationId, { getState }) => {
            const state = getState() as RootState;

            if (
              state.datasources.requestedOrganizationId === requestedOrganizationId
              && (isLoading(state.datasources.status) || isFinished(state.datasources.status))
            ) {
              return false;
            }
          },
        },
      },
    ),
    invalidateIntegrations: create.reducer((state) => {
      state.status = DataStatus.idle;
      state.requestedOrganizationId = undefined;
    }),
    addOrUpdateIntegration: create.reducer<Integration>((state, action) => {
      let updated = false;

      state.integrations = state.integrations.map((integration) => {
        if (integration.id === action.payload.id) {
          updated = true;

          return action.payload;
        }

        return integration;
      });

      if (!updated) {
        state.integrations.push(action.payload);
      }
    }),
    deleteIntegration: create.asyncThunk<string>(
      (id) => {
        return datasourcesAPI.deleteIntegration(id);
      },
      {
        fulfilled: (state, action) => {
          state.integrations = state.integrations.filter((integration) => {
            return integration.id !== action.meta.arg;
          });
        },
      },
    ),
  }),
  selectors: {
    selectIntegrations: (state) => {
      return state.integrations;
    },
    selectAppStoreConnectIntegrations: createSelector(
      (state: State) => state.integrations,
      (integrations) => {
        return integrations.filter((integration) => {
          return (
            integration.sourceIntegrationId ===
            APP_STORE_CONNECT_REPORTING_INTEGRATION_ID
          );
        }) as AppStoreConnectReportingIntegration[];
      },
    ),
    selectGooglePlayConsoleIntegrations: createSelector(
      (state: State) => state.integrations,
      (integrations) => {
        return integrations.filter((integration) => {
          return (
            integration.sourceIntegrationId ===
            GOOGLE_PLAY_CONSOLE_REPORTING_INTEGRATION_ID
          );
        }) as GooglePlayConsoleReportingIntegration[];
      },
    ),
    selectIsLoading: (state) => {
      return isLoading(state.status);
    },
    selectIsFinished: (state) => {
      return isFinished(state.status);
    },
  },
});

export const {
  setupIntegrations,
  addOrUpdateIntegration,
  deleteIntegration,
  invalidateIntegrations,
} = datasourcesSlice.actions;
export const {
  selectIsFinished,
  selectIsLoading,
  selectIntegrations,
  selectAppStoreConnectIntegrations,
  selectGooglePlayConsoleIntegrations,
} = datasourcesSlice.selectors;
